<template>
	<div :class="`border rounded-full uppercase ${sizes} ${colors}`">
		<slot/>
	</div>
</template>
<script setup lang="ts">
import {Color} from '@/Types/Enums/Color'
import {ButtonSize} from '@/Types/Enums/ButtonSize'
import {ref, type Ref, computed, type ComputedRef, defineProps} from 'vue'

const props = defineProps<{
	color?:Color,
	active?:boolean,
	size?:ButtonSize,
}>()

const active:Ref<boolean> = ref(props.active)

const sizes:ComputedRef<string> = computed(():string => {
	switch (props.size) {
		case ButtonSize.Small:
			return 'px-2 text-xs'
		case ButtonSize.Large:
			return 'px-3 py-1'
		case ButtonSize.Huge:
			return 'px-4 py-2 text-lg'
		default:
			return 'px-2 text-sm'
	}
})

const colors:ComputedRef<string> = computed(():string => {
	switch (props.color) {
		case Color.TrueAArEII:
			return active.value ? 'border-true-aareii-50 bg-true-aareii-50 text-true-aareii-800 dark:border-true-aareii-950 dark:bg-true-aareii-950 dark:text-true-aareii-200' : 'bg-transparent border-true-aareii-200 text-true-aareii-600 dark:border-true-aareii-950 dark:text-true-aareii-800'
		case Color.Red:
			return active.value ? 'border-red-50 bg-red-50 text-red-800 dark:border-red-950 dark:bg-red-950 dark:text-red-200' : 'bg-transparent border-red-200 text-red-600 dark:border-red-950 dark:text-red-800'
		case Color.Orange:
			return active.value ? 'border-orange-50 bg-orange-50 text-orange-800 dark:border-orange-950 dark:bg-orange-950 dark:text-orange-200' : 'bg-transparent border-orange-200 text-orange-600 dark:border-orange-950 dark:text-orange-800'
		case Color.Amber:
			return active.value ? 'border-amber-50 bg-amber-50 text-amber-800 dark:border-amber-950 dark:bg-amber-950 dark:text-amber-200' : 'bg-transparent border-amber-200 text-amber-600 dark:border-amber-950 dark:text-amber-800'
		case Color.Yellow:
			return active.value ? 'border-yellow-50 bg-yellow-50 text-yellow-800 dark:border-yellow-950 dark:bg-yellow-950 dark:text-yellow-200' : 'bg-transparent border-yellow-200 text-yellow-600 dark:border-yellow-950 dark:text-yellow-800'
		case Color.Lime:
			return active.value ? 'border-lime-50 bg-lime-50 text-lime-800 dark:border-lime-950 dark:bg-lime-950 dark:text-lime-200' : 'bg-transparent border-lime-200 text-lime-600 dark:border-lime-950 dark:text-lime-800'
		case Color.Green:
			return active.value ? 'border-green-50 bg-green-50 text-green-800 dark:border-green-950 dark:bg-green-950 dark:text-green-200' : 'bg-transparent border-green-200 text-green-600 dark:border-green-950 dark:text-green-800'
		case Color.Emerald:
			return active.value ? 'border-emerald-50 bg-emerald-50 text-emerald-800 dark:border-emerald-950 dark:bg-emerald-950 dark:text-emerald-200' : 'bg-transparent border-emerald-200 text-emerald-600 dark:border-emerald-950 dark:text-emerald-800'
		case Color.Teal:
			return active.value ? 'border-teal-50 bg-teal-50 text-teal-800 dark:border-teal-950 dark:bg-teal-950 dark:text-teal-200' : 'bg-transparent border-teal-200 text-teal-600 dark:border-teal-950 dark:text-teal-800'
		case Color.Cyan:
			return active.value ? 'border-cyan-50 bg-cyan-50 text-cyan-800 dark:border-cyan-950 dark:bg-cyan-950 dark:text-cyan-200' : 'bg-transparent border-cyan-200 text-cyan-600 dark:border-cyan-950 dark:text-cyan-800'
		case Color.Sky:
			return active.value ? 'border-sky-50 bg-sky-50 text-sky-800 dark:border-sky-950 dark:bg-sky-950 dark:text-sky-200' : 'bg-transparent border-sky-200 text-sky-600 dark:border-sky-950 dark:text-sky-800'
		case Color.Blue:
			return active.value ? 'border-blue-50 bg-blue-50 text-blue-800 dark:border-blue-950 dark:bg-blue-950 dark:text-blue-200' : 'bg-transparent border-blue-200 text-blue-600 dark:border-blue-950 dark:text-blue-800'
		case Color.Indigo:
			return active.value ? 'border-indigo-50 bg-indigo-50 text-indigo-800 dark:border-indigo-950 dark:bg-indigo-950 dark:text-indigo-200' : 'bg-transparent border-indigo-200 text-indigo-600 dark:border-indigo-950 dark:text-indigo-800'
		case Color.Violet:
			return active.value ? 'border-violet-50 bg-violet-50 text-violet-800 dark:border-violet-950 dark:bg-violet-950 dark:text-violet-200' : 'bg-transparent border-violet-200 text-violet-600 dark:border-violet-950 dark:text-violet-800'
		case Color.Purple:
			return active.value ? 'border-purple-50 bg-purple-50 text-purple-800 dark:border-purple-950 dark:bg-purple-950 dark:text-purple-200' : 'bg-transparent border-purple-200 text-purple-600 dark:border-purple-950 dark:text-purple-800'
		case Color.Fuchsia:
			return active.value ? 'border-fuchsia-50 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-950 dark:bg-fuchsia-950 dark:text-fuchsia-200' : 'bg-transparent border-fuchsia-200 text-fuchsia-600 dark:border-fuchsia-950 dark:text-fuchsia-800'
		case Color.Pink:
			return active.value ? 'border-pink-50 bg-pink-50 text-pink-800 dark:border-pink-950 dark:bg-pink-950 dark:text-pink-200' : 'bg-transparent border-pink-200 text-pink-600 dark:border-pink-950 dark:text-pink-800'
		case Color.Rose:
			return active.value ? 'border-rose-50 bg-rose-50 text-rose-800 dark:border-rose-950 dark:bg-rose-950 dark:text-rose-200' : 'bg-transparent border-rose-200 text-rose-600 dark:border-rose-950 dark:text-rose-800'
		case Color.Slate:
			return active.value ? 'border-slate-50 bg-slate-50 text-slate-800 dark:border-slate-950 dark:bg-slate-950 dark:text-slate-200' : 'bg-transparent border-slate-200 text-slate-600 dark:border-slate-950 dark:text-slate-800'
		case Color.Gray:
			return active.value ? 'border-gray-50 bg-gray-50 text-gray-800 dark:border-gray-950 dark:bg-gray-950 dark:text-gray-200' : 'bg-transparent border-gray-200 text-gray-600 dark:border-gray-950 dark:text-gray-800'
		case Color.Zinc:
			return active.value ? 'border-zinc-50 bg-zinc-50 text-zinc-800 dark:border-zinc-950 dark:bg-zinc-950 dark:text-zinc-200' : 'bg-transparent border-zinc-200 text-zinc-600 dark:border-zinc-950 dark:text-zinc-800'
		case Color.Stone:
			return active.value ? 'border-stone-50 bg-stone-50 text-stone-800 dark:border-stone-950 dark:bg-stone-950 dark:text-stone-200' : 'bg-transparent border-stone-200 text-stone-600 dark:border-stone-950 dark:text-stone-800'
		case Color.Neutral:
		default:
			return active.value ? 'border-neutral-50 bg-neutral-50 text-neutral-800 dark:border-neutral-950 dark:bg-neutral-950 dark:text-neutral-200' : 'bg-transparent border-neutral-200 text-neutral-600 dark:border-neutral-950 dark:text-neutral-800'
	}
})
</script>